import Zod, { z } from "zod";

const MAX_TEXT_FIELD_NAME_LENGTH = 50;
const MAX_TEXT_VALUE_LENGTH = 255;
const MAX_MULTI_VALUE_LENGTH = 100;

export enum MetadataFieldTypes {
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
  ENUM = "ENUM",
  ENUM_MULTI = "ENUM_MULTI",
  USER_LIST = "USER_LIST",
  STATE = "STATE",
  STATE_MULTI = "STATE_MULTI",
}

export const stringListForForm = z
  .array(z.string())
  .nullish()
  .transform(val => val ?? [])
  .or(
    z
      .string()
      .nullish()
      .transform(v => (v ? [v] : []))
  );

export const stringForForm = z
  .string()
  .nullish()
  .transform(v => v ?? "");

const BaseFieldSchema = z.object({
  id: z.string().uuid().nullish(),
  name: z
    .string()
    .min(3, "Field name is required")
    .max(
      MAX_TEXT_FIELD_NAME_LENGTH,
      `Field name must be ${MAX_TEXT_FIELD_NAME_LENGTH} characters or less`
    ),
  required: z.boolean(),
});

export type BaseFieldInput = z.infer<typeof BaseFieldSchema>;

export const MetadataStringFieldSchema = BaseFieldSchema.merge(
  z.object({
    maxLength: z.number().int().min(2).max(1000).default(MAX_TEXT_VALUE_LENGTH),
    defaultValue: z.string().max(MAX_TEXT_VALUE_LENGTH).nullish(),
  })
);

export const MetadataBoolFieldSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.boolean().nullish(),
  })
);

export const MetadataEnumFieldApiSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.string().nullable().default(null),
    values: z
      .array(
        z.string().min(1, "Field is required").max(MAX_TEXT_FIELD_NAME_LENGTH)
      )
      .min(1),
  })
);

export const MetadataEnumFieldUISchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.string().nullable(),
    values: z.array(
      z.object({
        value: z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH),
        key: z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH),
      })
    ),
  })
);

export const MetadataEnumMultiFieldApiSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.array(z.string()).nullish().default([]),
    values: z.array(z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH)),
  })
);

export const MetadataEnumMultiFieldUISchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.array(z.string()),
    values: z.array(
      z.object({
        value: z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH),
        key: z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH),
      })
    ),
  })
);

export const MetadataUserListFieldSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: z.literal(null).default(null),
  })
);

export const MetadataStateFieldUISchema = BaseFieldSchema.merge(
  z.object({
    defaultListValue: z.array(z.string()),
    defaultValue: stringForForm,
    values: z.array(
      z.object({
        value: z.string().min(1).max(MAX_TEXT_FIELD_NAME_LENGTH),
      })
    ),
    isSingleSelect: z.boolean(),
  })
);

export const MetadataStateFieldApiSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: stringListForForm.transform(v => {
      if (v.length === 0) {
        return null;
      }
      // if there's an empty string, return null
      return v[0] || null;
    }),
    values: z
      .array(
        z.string().min(1, "Field is required").max(MAX_TEXT_FIELD_NAME_LENGTH)
      )
      .min(1),
    isSingleSelect: z.boolean().default(false),
  })
);

export const MetadataStateMultiFieldApiSchema = BaseFieldSchema.merge(
  z.object({
    defaultValue: stringListForForm,
    values: z
      .array(
        z.string().min(1, "Field is required").max(MAX_TEXT_FIELD_NAME_LENGTH)
      )
      .min(1),
    isSingleSelect: z.boolean().default(false),
  })
);
export const CreateOrUpdateMetadataFieldSchema = z.discriminatedUnion("type", [
  MetadataEnumFieldApiSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.ENUM) })
  ),
  MetadataEnumMultiFieldApiSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.ENUM_MULTI) })
  ),
  MetadataStateMultiFieldApiSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.STATE_MULTI) })
  ),
  MetadataStateFieldApiSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.STATE) })
  ),
  // In some cases, we can use the same form schema for both the API and the FE.
  MetadataUserListFieldSchema.merge(
    z.object({
      type: z.literal(MetadataFieldTypes.USER_LIST),
    })
  ),
  MetadataStringFieldSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.STRING) })
  ),
  MetadataBoolFieldSchema.merge(
    z.object({ type: z.literal(MetadataFieldTypes.BOOLEAN) })
  ),
]);

export type CreateOrUpdateMetadataFieldSchemaOutput = z.infer<
  typeof CreateOrUpdateMetadataFieldSchema
>;

// Used on the backend when receiving input from Forms which do not store null values.
// empty strings and empty arrays are switched to nulls so the check for whether
// a value is present or not is consistent across types.
export const UpdateMetadataValueSchema = z.object({
  campaignId: z.string().uuid(),
  fieldValues: z.array(
    z.object({
      fieldId: z.string().uuid(),
      id: z.string().uuid().nullish(),
      stringValue: z
        .string()
        .max(MAX_TEXT_VALUE_LENGTH)
        .nullish()
        // transform empty strings to null
        .transform(v => v || null),
      boolValue: z.boolean().nullish().default(null),
      stringListValue: z
        .array(z.string().max(MAX_TEXT_VALUE_LENGTH))
        .nullish()
        // transform empty arrays to null
        .transform(v => (v?.length ? v : null)),
      uuidValue: z.preprocess(
        v => (v === "" ? null : v),
        z.string().uuid().nullish().default(null)
      ),
    })
  ),
});

export type UpdateMetadataValueSchemaOutput = z.infer<
  typeof UpdateMetadataValueSchema
>;

export type UpdateMetadataValueSchemaInput = z.input<
  typeof UpdateMetadataValueSchema
>;

export const userForForm = z.object({
  userId: z.string(),
  fullName: z.string(),
});

export type UserSchemaOutput = z.infer<typeof userForForm>;

export type UserListValueSchemaOutput = z.infer<typeof userForForm>;

// Same schema used on backend / FE to serialize / deserialize.
export const MetadataValueBaseSchema = z.object({
  fieldId: z.string().uuid(),
  // A value id may be null if the field hasn't populated yet.
  id: z.string().uuid().nullable().default(null),
  required: z.boolean(),
  name: z.string(),
  createdAt: z.date().nullable().default(null),
  updatedAt: z.date().nullable().default(null),
  archivedAt: z.date().nullable().default(null),
});

const MetadataFieldBaseSchema = z.object({
  required: z.boolean(),
});

export const StringConfigurationSchema = z.object({ maxLength: z.number() });

const MetadataStringSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.STRING),
  configuration: StringConfigurationSchema,
});

export const MetadataStringValueSchema = MetadataStringSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: stringForForm,
  value: stringForForm,
});

const MetadataBoolSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.BOOLEAN),
  configuration: z.object({}).nullish().default({}),
});

export const MetadataBoolValueSchema = MetadataBoolSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: z.boolean().nullable().default(null),
  value: z.boolean().nullable().default(null),
});

export const EnumConfigurationSchema = z.object({
  values: stringListForForm,
  archived: stringListForForm,
});

const MetadataEnumSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.ENUM),
  configuration: EnumConfigurationSchema,
});

export const MetadataEnumValueSchema = MetadataEnumSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: stringForForm,
  value: stringForForm,
});

const MetadataEnumMultiSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.ENUM_MULTI),
  configuration: EnumConfigurationSchema,
});

export const MetadataEnumMultiValueSchema = MetadataEnumMultiSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: stringListForForm,
  value: stringListForForm,
});

const MetadataStateSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.STATE),
  configuration: EnumConfigurationSchema,
});

export const MetadataStateValueSchema = MetadataStateSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: stringForForm,
  value: stringForForm,
});

const MetadataStateMultiSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.STATE_MULTI),
  configuration: EnumConfigurationSchema,
});

export const MetadataStateMultiValueSchema = MetadataStateMultiSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: stringListForForm,
  value: stringListForForm,
});

const MetadataUserListSchema = MetadataFieldBaseSchema.extend({
  type: z.literal(MetadataFieldTypes.USER_LIST),
  configuration: z.object({}).nullish().default({}),
});

export const MetadataUserListValueSchema = MetadataUserListSchema.merge(
  MetadataValueBaseSchema
).extend({
  defaultValue: userForForm
    .nullish()
    .transform(v => v ?? { fullName: "", userId: "" }),
  value: userForForm
    .nullish()
    .transform(v => v ?? { fullName: "", userId: "" }),
});

export const MetadataValueSchema = z.discriminatedUnion("type", [
  MetadataStringValueSchema,
  MetadataBoolValueSchema,
  MetadataEnumValueSchema,
  MetadataEnumMultiValueSchema,
  MetadataUserListValueSchema,
  MetadataStateValueSchema,
  MetadataStateMultiValueSchema,
]);

export const MetadataFieldSchema = z.discriminatedUnion("type", [
  MetadataStringSchema,
  MetadataBoolSchema,
  MetadataEnumSchema,
  MetadataEnumMultiSchema,
  MetadataUserListSchema,
  MetadataStateSchema,
  MetadataStateMultiSchema,
]);

export type MetadataFieldSchemaOutput = z.infer<typeof MetadataFieldSchema>;

export type MetadataValueBaseSchemaOutput = z.infer<
  typeof MetadataValueBaseSchema
>;
export type MetadataEnumValueSchemaOutput = z.infer<
  typeof MetadataEnumValueSchema
>;
export type MetadataEnumMultiSchemaOutput = z.infer<
  typeof MetadataEnumMultiValueSchema
>;
export type MetadataStringValueSchemaOutput = z.infer<
  typeof MetadataStringValueSchema
>;
export type EnumConfigurationSchemaOutput = z.infer<
  typeof EnumConfigurationSchema
>;
export type StringConfigurationSchemaOutput = z.infer<
  typeof StringConfigurationSchema
>;
export type MetadataBoolValueSchemaOutput = z.infer<
  typeof MetadataBoolValueSchema
>;
export type MetadataUserListValueSchemaOutput = z.infer<
  typeof MetadataUserListValueSchema
>;
export type MetadataStateValueSchemaOutput = z.infer<
  typeof MetadataStateValueSchema
>;
export type MetadataStateMultiValueSchemaOutput = z.infer<
  typeof MetadataStateMultiValueSchema
>;
export type MetadataValueSchemaOutput = z.infer<typeof MetadataValueSchema>;
export type MetadataValueSchemaInput = z.input<typeof MetadataValueSchema>;
interface BaseValueSchemaParams {
  required: boolean;
}

export type MetadataFieldValueProperties<
  Value,
  TypeParameters extends BaseValueSchemaParams,
  Input = Value,
> = {
  valueSchema: (
    typeParameters: TypeParameters
  ) =>
    | z.ZodType<Value, z.ZodTypeDef, Input>
    | Zod.ZodEffects<Zod.ZodString, string | null, string>;
};

export const SchemaDefinitionByType = {
  STRING: {
    valueSchema: (
      params?: StringConfigurationSchemaOutput & BaseValueSchemaParams
    ) =>
      z
        .string()
        .min(params?.required ? 1 : 0, "Field is required")
        .max(params?.maxLength || MAX_TEXT_VALUE_LENGTH)
        .transform(value => {
          if (value === null) {
            return null;
          }
          return value.trim();
        }),
  } satisfies MetadataFieldValueProperties<
    string,
    StringConfigurationSchemaOutput & BaseValueSchemaParams
  >,
  USER_LIST: {
    valueSchema: params => {
      return z.object({
        userId: z
          .string()
          .min(params?.required ? 1 : 0, "Field is required")
          .max(MAX_TEXT_VALUE_LENGTH),
        fullName: z
          .string()
          .min(params?.required ? 1 : 0, "Field is required")
          .max(MAX_TEXT_VALUE_LENGTH),
      });
    },
  } satisfies MetadataFieldValueProperties<
    UserSchemaOutput,
    BaseValueSchemaParams
  >,
  BOOLEAN: {
    valueSchema: (params?: BaseValueSchemaParams) => {
      const schema = z.boolean({ message: "Field is required" });
      if (!params?.required) {
        return schema.nullable();
      }
      return schema;
    },
  } satisfies MetadataFieldValueProperties<
    boolean | null,
    BaseValueSchemaParams
  >,
  ENUM: {
    valueSchema: (
      params: EnumConfigurationSchemaOutput & BaseValueSchemaParams
    ) =>
      z.enum(
        [
          ...(params.required ? [] : [""]),
          ...params.values,
          ...params.archived,
        ] as [string, ...string[]],
        {
          message: "Selection is required",
        }
      ),
  } satisfies MetadataFieldValueProperties<
    string | null,
    EnumConfigurationSchemaOutput & BaseValueSchemaParams
  >,
  ENUM_MULTI: {
    valueSchema: (
      params: EnumConfigurationSchemaOutput & BaseValueSchemaParams
    ) =>
      z
        .array(
          z.enum([...params.values, ...params.archived] as [
            string,
            ...string[],
          ])
        )
        .min(params?.required ? 1 : 0, { message: "Selection is required" })
        .max(MAX_MULTI_VALUE_LENGTH, {
          message: `You can select up to ${MAX_MULTI_VALUE_LENGTH} options.`,
        }),
  } satisfies MetadataFieldValueProperties<
    string[] | null,
    EnumConfigurationSchemaOutput & BaseValueSchemaParams
  >,
};

export function createMetadataFieldValueSchema(
  field: MetadataFieldSchemaOutput
): Zod.Schema {
  let fieldSchema: Zod.Schema;

  switch (field.type) {
    case MetadataFieldTypes.STRING:
      fieldSchema = SchemaDefinitionByType[field.type].valueSchema({
        ...field.configuration,
        required: field.required,
      });
      break;

    case MetadataFieldTypes.ENUM:
    case MetadataFieldTypes.STATE:
      fieldSchema = SchemaDefinitionByType.ENUM.valueSchema({
        ...field.configuration,
        required: field.required,
      });
      break;
    case MetadataFieldTypes.ENUM_MULTI:
    case MetadataFieldTypes.STATE_MULTI:
      fieldSchema = SchemaDefinitionByType.ENUM_MULTI.valueSchema({
        ...field.configuration,
        required: field.required,
      });
      break;

    default:
      fieldSchema = SchemaDefinitionByType[field.type].valueSchema({
        required: field.required,
      });
  }

  if (!field.required) {
    fieldSchema = fieldSchema.nullable().optional();
  }

  return fieldSchema;
}

export function metadataFieldFromDb(dbField: {
  type: keyof typeof MetadataFieldTypes;
  configuration: any;
  required: boolean;
}): MetadataFieldSchemaOutput {
  return {
    type: MetadataFieldTypes[dbField.type],
    configuration: dbField.configuration,
    required: dbField.required,
  };
}
